import React, { useContext, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import Choices from '../../../../components/Choices';
import { useTranslation } from 'react-i18next';
import { useHandHygiene } from '../hooks/use_hand_hygiene.js';
import UserContext from '../../../../components/User_Context.js';
import { useParams } from 'react-router-dom';
import { toDate, toNumber } from '../../../../generalUtils';
import Swal from 'sweetalert2';

const MainDataSection = forwardRef(({
    sheet,
    vocabs,
    filterAreasByDepartment,
    filterDepartmentsByAuthority,
    onDataChange
}, ref) => {
    const { t } = useTranslation(['labels']);
    const today = new Date().toISOString().split('T')[0];
    const { currentUser } = useContext(UserContext);
    const { id } = useParams();

    // Local state for main data
    const [departmentType, setDepartmentType] = useState(sheet.DepartmentType || '');
    const [authority, setAuthority] = useState(null);
    const [department, setDepartment] = useState(sheet.Department || '');
    const [functionalArea, setFunctionalArea] = useState(sheet.FunctionalArea || '');
    const [startObservation, setStartObservation] = useState(sheet.StartObservation || '');
    const [endObservation, setEndObservation] = useState(sheet.EndObservation || '');
    const [sessionLength, setSessionLength] = useState(sheet.SessionLength || null);

    // Update authority state when sheet changes
    useEffect(() => {
        if (!vocabs.authorities || vocabs.authorities.length === 0) {
            // When no authorities in vocabs, use the saved value or env
            const currentAuthority = sheet.HealthAuthority || null;
            setAuthority(currentAuthority);
            console.log('Authority (no vocabs):', currentAuthority);
        } else {
            // When authorities are available, update the state with the saved value
            const healthAuth = sheet.HealthAuthority || null;
            setAuthority(healthAuth);
            console.log('Authority (with vocabs):', healthAuth, 'Available authorities:', vocabs.authorities);
        }
    }, [sheet.HealthAuthority, vocabs.authorities]);

    const {
        startTimeRef,
        endTimeRef
    } = useHandHygiene(id, currentUser);

    // Expose data collection method to parent
    useImperativeHandle(ref, () => ({
        getData: () => ({
            DepartmentType: departmentType,
            HealthAuthority: authority,
            Department: department,
            FunctionalArea: functionalArea,
            StartObservation: startObservation,
            EndObservation: endObservation,
            SessionLength: sessionLength
        })
    }));

    // Helper: calculate session duration in minutes
    const calculateSessionDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return null;

        const startDate = new Date(startTime);
        const endDate = new Date(endTime);
        const diffMin = Math.round((endDate - startDate) / 60000);

        return diffMin >= 0 ? diffMin : null;
    };

    // Helper: format ISO string into YYYY-MM-DDThh:mm for datetime-local (maintaining local time)
    const formatDateTimeLocal = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        // Reset seconds and milliseconds to ensure clean formatting
        date.setSeconds(0, 0);
        return formatLocalDateTime(date);
    };

    // Helper: format local Date into YYYY-MM-DDThh:mm for datetime-local
    const formatLocalDateTime = (date = new Date()) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Calculate the "now" HH:MM string
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const nowStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const handleAuthorityChange = (selection) => {
        // Get the selected value, ensuring null for empty selections
        const selectedItem = selection.selected?.[0];
        const newAuth = selectedItem?.Id || null;

        // If the selected item has Id as null or undefined, explicitly set to null
        const finalValue = (newAuth === null || newAuth === undefined || newAuth === '') ? null : newAuth;

        setAuthority(finalValue);
        // Reset department and functional area when authority changes
        setDepartment(null);
        setFunctionalArea(null);
        if (finalValue && filterDepartmentsByAuthority) {
            filterDepartmentsByAuthority(finalValue);
        }
        onDataChange();
    };

    const handleDepartmentChange = (selection) => {
        // Get the selected value, ensuring null for empty selections
        const selectedItem = selection.selected?.[0];
        const newDept = selectedItem?.Id || null;

        // If the selected item has Id as null or undefined, explicitly set to null
        const finalValue = (newDept === null || newDept === undefined || newDept === '') ? null : newDept;

        setDepartment(finalValue);
        // Reset functional area when department changes
        setFunctionalArea(null);
        if (finalValue) filterAreasByDepartment(finalValue);
        onDataChange();
    };

    const handleFunctionalAreaChange = (selection) => {
        // Get the selected value, ensuring null for empty selections
        const selectedItem = selection.selected?.[0];
        const newArea = selectedItem?.Id || null;

        // If the selected item has Id as null or undefined, explicitly set to null
        const finalValue = (newArea === null || newArea === undefined || newArea === '') ? null : newArea;

        setFunctionalArea(finalValue);
        onDataChange();
    };

    // Set end time to current time and update duration
    const handleSetEndTime = () => {
        const now = new Date();
        const endDateTimeLocal = formatLocalDateTime(now);

        // Update the end time input value
        if (endTimeRef.current) {
            endTimeRef.current.value = endDateTimeLocal;
        }

        // Get the start time value
        const startVal = startTimeRef.current?.value;
        if (!startVal) {
            Swal.fire({
                title: t('attention'),
                icon: 'warning',
                text: t('startDateTimeRequired'),
            });
            return;
        }

        // Calculate duration
        const startDate = new Date(startVal);
        const endDate = new Date(endDateTimeLocal);

        // Validate that end date is not in the future (should never happen with current time, but for safety)
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (endDate > today) {
            Swal.fire({
                title: t('attention'),
                icon: 'warning',
                text: t('endDateCannotBeFuture', 'La data di fine osservazione non può essere successiva al giorno odierno'),
            });
            return;
        }

        const duration = calculateSessionDuration(startDate.toISOString(), endDate.toISOString());

        // Update local state
        setEndObservation(endDate.toISOString());
        setSessionLength(duration);
        onDataChange();
    };

    return (
        <div className="container-fluid px-0">
            <div className="row g-3 align-items-stretch">
                {/* LEFT COLUMN */}
                <div className="col-12 col-md-6 col-xl-4">
                    <div className="p-3 border rounded bg-light h-100">
                        <div className="mb-3">
                            <label className="form-label fw-bold">
                                {t('healthAuthority')}<span className="text-danger">*</span>
                            </label>
                            {!id && vocabs.authorities && vocabs.authorities.length > 0 ? (
                                <Choices
                                    voc={vocabs.authorities}
                                    value="Id"
                                    text="Name"
                                    className="inputStyle orange-select blackText"
                                    selected={
                                        authority
                                            ? vocabs.authorities.filter(a => String(a.Id) === String(authority))
                                            : []
                                    }
                                    onChange={handleAuthorityChange}
                                />
                            ) : sheet.HealthAuthority && vocabs.authorities && vocabs.authorities.length > 0 ? (
                                <Choices
                                    voc={vocabs.authorities}
                                    value="Id"
                                    text="Name"
                                    className="inputStyle orange-select blackText"
                                    selected={
                                        sheet.HealthAuthority
                                            ? vocabs.authorities.filter(a => String(a.Id) === String(sheet.HealthAuthority))
                                            : []
                                    }
                                    onChange={handleAuthorityChange}
                                />
                            ) : <input
                                type="text"
                                className="form-control inputStyle"
                                readOnly={!sheet.HealthAuthority}
                                value={sheet.HealthAuthority || process.env.REACT_APP_ORGANIZATION}
                            />}

                        </div>
                        <div className="mb-3">
                            <label className="form-label fw-bold">
                                {t('departmentCategory')}<span className="text-danger">*</span>
                            </label>
                            <Choices
                                voc={vocabs.departmentCategories}
                                value="Code"
                                text="Name"
                                className="inputStyle orange-select blackText"
                                selected={vocabs.departmentCategories.filter(c => c.Code === departmentType)}
                                onChange={sel => {
                                    const code = sel.selected?.[0]?.Code || null;
                                    setDepartmentType(code);
                                    onDataChange();
                                }}
                            />
                        </div>
                        <div>
                            <label className="form-label fw-bold">{t('lastModifyDate')}</label>
                            <input
                                type="date"
                                className="form-control inputStyle"
                                readOnly
                                value={
                                    sheet.UpdatedAt
                                        ? new Date(sheet.UpdatedAt).toISOString().split('T')[0]
                                        : sheet.CreatedAt
                                            ? new Date(sheet.CreatedAt).toISOString().split('T')[0]
                                            : today
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* CENTRAL COLUMN */}
                <div className="col-12 col-md-6 col-xl-4">
                    <div className="p-3 border rounded bg-light h-100">
                        <div className="mb-3">
                            <label className="form-label fw-bold">{t('observer')}</label>
                            <input
                                type="text"
                                className="form-control inputStyle"
                                readOnly
                                value={sheet.ObserverFullName}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label fw-bold">
                                {t('department')}<span className="text-danger">*</span>
                            </label>
                            <Choices
                                voc={vocabs.filteredDepartments}
                                value="Id"
                                text="Name"
                                className="inputStyle orange-select blackText"
                                selected={department ? vocabs.filteredDepartments.filter(d => d.Id === department) : []}
                                onChange={handleDepartmentChange}
                            />
                        </div>
                        <div>
                            <label className="form-label fw-bold">
                                {t('functionalArea')}<span className="text-danger">*</span>
                            </label>
                            <Choices
                                voc={vocabs.filteredAreas}
                                value="Id"
                                text="Name"
                                className="inputStyle orange-select blackText"
                                selected={functionalArea ? vocabs.filteredAreas.filter(a => a.Id === functionalArea) : []}
                                onChange={handleFunctionalAreaChange}
                            />
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="col-12 col-md-6 offset-md-3 col-xl-4 offset-xl-0">
                    <div className="p-3 border rounded bg-light h-100">
                        <div className="mb-3">
                            <label className="form-label fw-bold">
                                {t('startEnd')}<span className="text-danger">*</span>
                            </label>
                            <div className="row g-2">
                                <div className="col-6">
                                    <input
                                        type="datetime-local"
                                        step="60"
                                        className="form-control inputStyle"
                                        ref={(r) => {
                                            startTimeRef.current = r;
                                            if (r) r.valueAsNumber = toNumber(startObservation);
                                        }}
                                        value={startObservation ? formatDateTimeLocal(startObservation) : formatDateTimeLocal(today)}
                                        onChange={(e) => {
                                            const _newValue = e.target.valueAsNumber;
                                            const currentValue = toNumber(startObservation);

                                            if (!isNaN(_newValue) && _newValue !== currentValue) {
                                                const newStartTime = toDate(_newValue);
                                                setStartObservation(newStartTime);
                                                setSessionLength(calculateSessionDuration(newStartTime, endObservation));
                                            } else if (isNaN(_newValue) && startObservation) {
                                                setStartObservation(null);
                                                setSessionLength(null);
                                            }
                                            onDataChange();
                                        }}
                                    />
                                </div>
                                <div className="col-6">
                                    <input
                                        type="datetime-local"
                                        step="60"
                                        className="form-control inputStyle"
                                        ref={(r) => {
                                            endTimeRef.current = r;
                                            if (r) r.valueAsNumber = toNumber(endObservation);
                                        }}
                                        value={formatDateTimeLocal(endObservation)}
                                        onChange={(e) => {
                                            const _newValue = e.target.valueAsNumber;
                                            const currentValue = toNumber(endObservation);

                                            if (!isNaN(_newValue) && _newValue !== currentValue) {
                                                const newEndTime = toDate(_newValue);
                                                const today = new Date();
                                                today.setHours(23, 59, 59, 999); // Set to end of today

                                                // Check if the selected end time is after today
                                                if (newEndTime > today) {
                                                    Swal.fire({
                                                        title: t('attention'),
                                                        icon: 'warning',
                                                        text: t('endDateCannotBeFuture', 'La data di fine osservazione non può essere successiva al giorno odierno'),
                                                    });
                                                    // Reset to previous value
                                                    if (endTimeRef.current) {
                                                        endTimeRef.current.valueAsNumber = currentValue;
                                                    }
                                                    return;
                                                }

                                                setEndObservation(newEndTime);
                                                setSessionLength(calculateSessionDuration(startObservation, newEndTime));
                                            } else if (isNaN(_newValue) && endObservation) {
                                                setEndObservation(null);
                                                setSessionLength(null);
                                            }
                                            onDataChange();
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-3">
                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={handleSetEndTime}
                                title={t('setCurrentTime')}
                            >
                                <i className="fa fa-clock me-2"></i>
                                {t('setCurrentTime')}
                            </button>
                        </div>
                        <div className="mb-3">
                            <label className="form-label fw-bold">{t('sessionLength')}</label>
                            <input
                                type="text"
                                className="form-control inputStyle"
                                readOnly
                                value={
                                    sessionLength != null && !isNaN(sessionLength) && sessionLength >= 0
                                        ? String(sessionLength)
                                        : ''
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default MainDataSection;
